import {
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Typography,
} from 'antd';

import {
  VehicleHistoryDetailFormValues,
  VehicleHistoryDetailQuery,
  VehicleHistoryItemsDetail,
} from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.types';
import { FloatingFormItem } from '@/components/_common';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { quantityFormatter } from '@/utils/number';
import Table, { ColumnsType } from 'antd/lib/table';
import { getTableScrollX } from '@/utils/table';
import { v4 } from 'uuid';
import { useDebounce, useRouteMatchParam, useWindowSize } from '@/hooks';
import { useState } from 'react';
import { useVehicleHistoryDetailQuery } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.hooks';
import StatusTag from '@/components/_common/StatusTag';
import { workorderComplementColor } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterNumber from '@/utils/number/formatterNumber';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  RegistrationsDefaultCode,
  RegistrationsDefaultCodeLabel,
} from '@/types/Register';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import { VehicleHistoryOrderByColumn } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.enums';
import { VehicleHistoryOrderByColumnLabel } from '@/core/domain/workorder/VehiclesHistory/VehiclesHistory.constants';

const { Title, Text } = Typography;

interface Props {
  setHasAuthorized: (value: boolean) => void;
}

const VehicleHistoryDetails: React.FC<Props> = ({ setHasAuthorized }) => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const [form] = Form.useForm<VehicleHistoryDetailFormValues>();

  const licensePlate = useRouteMatchParam('licensePlate');
  const [params, setParams] = useState<VehicleHistoryDetailQuery>({
    licensePlate,
    order: 1,
  });

  const matrixSettings = useMatrix();

  const defaultPartCode = matrixSettings.data?.matrixSettings.defaultPartCode;

  const partCodeColumns = {
    [RegistrationsDefaultCode.barCode]: 'barCode',
    [RegistrationsDefaultCode.companyCode]: 'companyCode',
    [RegistrationsDefaultCode.internalCode]: 'sku',
    [RegistrationsDefaultCode.manufacturerCode]: 'manufacturingCode',
    [RegistrationsDefaultCode.sku]: 'sku',
  };

  const partCodeKey =
    partCodeColumns[defaultPartCode as RegistrationsDefaultCode];

  const paramsDebounced = useDebounce(params, 1000);

  const { data: vehicleHistoryDetailData, isLoading } =
    useVehicleHistoryDetailQuery(paramsDebounced);

  const vehicleFilter = vehicleHistoryDetailData?.vehicleHistory?.filter(
    ({ currentKilometers }) =>
      currentKilometers !== undefined || currentKilometers !== null,
  );

  const averageMax = isNaN(Number(vehicleFilter?.at(0)?.currentKilometers))
    ? 0
    : Number(vehicleFilter?.at(0)?.currentKilometers);

  const averageMin = isNaN(Number(vehicleFilter?.at(-1)?.currentKilometers))
    ? 0
    : Number(vehicleFilter?.at(-1)?.currentKilometers);

  const firstDate = moment(vehicleFilter?.at(-1)?.entryDate).format(
    'DD/MM/YYYY',
  );

  const lastDate = moment(vehicleFilter?.at(0)?.entryDate).format('DD/MM/YYYY');

  const diffDays = moment(vehicleFilter?.at(0)?.entryDate).diff(
    vehicleFilter?.at(-1)?.entryDate,
    'days',
  );

  const handleValuesChanged = (
    _: VehicleHistoryDetailFormValues,
    values: VehicleHistoryDetailFormValues,
  ) => {
    if (values) {
      setParams((prev) => ({
        ...prev,
        authorized: values.authorized,
        search: values.description,
      }));
      setHasAuthorized(values.authorized);
    }
  };

  const columns: ColumnsType<VehicleHistoryItemsDetail> = [
    {
      dataIndex: 'itemDescription',
      key: 'itemDescription',
      title: intl.formatMessage({
        id: 'vehiclesHistory.details.list.column.description',
      }),
      ellipsis: true,
      width: 220,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: partCodeKey,
      key: partCodeKey,
      title:
        RegistrationsDefaultCodeLabel[
          defaultPartCode as RegistrationsDefaultCode
        ],
      ellipsis: true,
      width: 220,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'quantity',
      key: 'quantity',
      title: intl.formatMessage({
        id: 'vehiclesHistory.details.list.column.quantity',
      }),
      ellipsis: true,
      width: 220,
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      title: intl.formatMessage({
        id: 'vehiclesHistory.details.list.column.unitaryValue',
      }),
      ellipsis: true,
      width: 140,
      render: (value) => `R$ ${quantityFormatter(value ?? 0)}`,
    },
    {
      dataIndex: 'discount',
      key: 'discount',
      title: intl.formatMessage({
        id: 'vehiclesHistory.details.list.column.discount',
      }),
      ellipsis: true,
      width: 140,
      render: (value) => `R$ ${quantityFormatter(value ?? 0)}`,
    },
    {
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      title: intl.formatMessage({
        id: 'vehiclesHistory.details.list.column.totalAmount',
      }),
      ellipsis: true,
      width: 140,
      render: (value) => `R$ ${quantityFormatter(value ?? 0)}`,
    },
  ];
  const averageKm =
    'KM ' +
    (diffDays !== 0
      ? formatterNumber(((averageMax - averageMin) / diffDays) * 30, {
          minimumFractionDigits: 0,
        }).split(',')[0]
      : '-');

  return (
    <Skeleton loading={isLoading}>
      <Card size="small">
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={24} xl={14}>
            <Title level={4} style={{ marginBottom: 0 }}>
              {vehicleHistoryDetailData?.vehicleModel}
            </Title>
          </Col>

          <Col span={24}>
            <Row gutter={[16, 0]}>
              <Col lg={8}>
                <Text strong>
                  Combustível: {vehicleHistoryDetailData?.vehicleFuel}
                </Text>
              </Col>

              <Col lg={8}>
                <Text strong>
                  Ano de fabricação/modelo:{' '}
                  {`${vehicleHistoryDetailData?.vehicleYearFabrication}/${vehicleHistoryDetailData?.vehicleYearModel}`}
                </Text>
              </Col>

              {vehicleHistoryDetailData?.vehicleFleet && (
                <Col lg={8}>
                  <Text strong>
                    Frota: {vehicleHistoryDetailData?.vehicleFleet}
                  </Text>
                </Col>
              )}
            </Row>
          </Col>

          <Col span={24}>
            <Row gutter={[16, 0]}>
              <Col lg={8}>
                <Statistic
                  title={`Primeira visita em ${firstDate}`}
                  value={`KM ${formatterNumber(averageMin, {
                    minimumFractionDigits: 0,
                  })}`}
                />
              </Col>

              <Col lg={8}>
                <Statistic
                  title={'Ultima visita em ' + lastDate}
                  value={`KM ${formatterNumber(averageMax, {
                    minimumFractionDigits: 0,
                  })}`}
                />
              </Col>

              <Col lg={8}>
                <Statistic title="Média por mês" value={averageKm} />
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <Title level={4} style={{ marginBottom: 0 }}>
              Filtros
            </Title>
          </Col>

          <Col span={24}>
            <Form
              layout="vertical"
              form={form}
              onValuesChange={handleValuesChanged}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} md={16} xl={15}>
                  <FloatingFormItem
                    name="description"
                    label={intl.formatMessage({
                      id: 'vehiclesHistory.details.searchByDescription',
                    })}
                    style={{ marginBottom: 0 }}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>

                <Col xs={24} md={8} xl={3}>
                  <FloatingFormItem name="orderByColumn" label="Ordenar por">
                    <Select
                      options={Object.values(VehicleHistoryOrderByColumn).map(
                        (orderByColumn) => ({
                          label:
                            VehicleHistoryOrderByColumnLabel[orderByColumn],
                          value: orderByColumn,
                        }),
                      )}
                      onChange={(value) =>
                        setParams((prev) => ({ ...prev, orderByColumn: value }))
                      }
                    />
                  </FloatingFormItem>
                </Col>

                <Col xs={24} md={8} xl={3}>
                  <FloatingFormItem
                    name="order"
                    label="Tipo de ordem"
                    initialValue={1}
                  >
                    <OrderExtraFilter
                      value={params?.order}
                      placeholder
                      optionsValue={{ asc: 0, desc: 1 }}
                      onChange={(value) =>
                        setParams((prev) => ({ ...prev, order: value }))
                      }
                    />
                  </FloatingFormItem>
                </Col>

                <Col
                  xs={24}
                  md={8}
                  xl={3}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item
                    name="authorized"
                    initialValue={false}
                    valuePropName="checked"
                  >
                    <Checkbox>
                      {intl.formatMessage({
                        id: 'vehiclesHistory.details.displayAuthorized',
                      })}
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Card>

      {vehicleHistoryDetailData?.vehicleHistory?.map((vehicleHistoryDetail) => {
        if (vehicleHistoryDetail.items.length) {
          return (
            <Card style={{ marginTop: '2em' }}>
              <>
                <Title>
                  <Descriptions column={{ xs: 1, md: 2, lg: 4, xxl: 4 }}>
                    <Descriptions.Item label={'Data de Entrada'}>
                      {vehicleHistoryDetail.entryDate
                        ? moment(vehicleHistoryDetail.entryDate).format(
                            'DD/MM/YYYY',
                          )
                        : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={
                        isMobile
                          ? 'O.S'
                          : intl.formatMessage({
                              id: 'vehiclesHistory.details.list.info.budget',
                            })
                      }
                    >
                      <Space>
                        {`${vehicleHistoryDetail.workOrderNumber}/${vehicleHistoryDetail.complementNumber}`}

                        <StatusTag
                          color={
                            workorderComplementColor[
                              vehicleHistoryDetail.status
                            ]
                          }
                          label={intl.formatMessage({
                            id: `workOrders.${vehicleHistoryDetail?.status}`,
                          })}
                        />
                      </Space>
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={intl.formatMessage({
                        id: 'vehiclesHistory.details.list.info.customer',
                      })}
                    >
                      {vehicleHistoryDetail.customer ?? '-'}
                    </Descriptions.Item>

                    <Descriptions.Item
                      label={intl.formatMessage({
                        id: 'vehiclesHistory.details.list.info.currentKilometers',
                      })}
                    >
                      {formatterNumber(
                        Number(vehicleHistoryDetail.currentKilometers),
                        { minimumFractionDigits: 0 },
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Title>

                <Table<VehicleHistoryItemsDetail>
                  rowKey={v4()}
                  columns={columns}
                  dataSource={vehicleHistoryDetail.items}
                  scroll={{ x: getTableScrollX(columns) }}
                  pagination={false}
                />
              </>
            </Card>
          );
        }
      })}
    </Skeleton>
  );
};

export default VehicleHistoryDetails;
